/*
<purpose>
  Owns server-side, cursor-paginated active-ticket data for DashboardSeite/active
  list: rows, the next-page cursor, the three per-bucket counts, plus
  loading/error state and an IntersectionObserver sentinelRef for infinite scroll.
  Twin of useArchivedPagination — swaps bucket/sort vocabulary + counts shape.
</purpose>
<dependencies>smarttix.getActivePage (services/api.ts)</dependencies>
<constraints>
  - First page fetched on mount and re-fetched whenever bucket, sort, or any filter
    changes; cursor resets on every such change (no stale append across buckets).
  - loadMore() is a no-op while a fetch is in flight or when no nextCursor exists.
  - Appends de-dupe by uuid so an over-eager sentinel can't double-insert rows.
  - sentinelRef is a callback ref: attaches/detaches cleanly as the sentinel node
    mounts/unmounts (after first-page load); loadMore is read via a ref to avoid
    re-creating the IntersectionObserver on every render.
  - refresh() is a silent background re-fetch of page 1: never clears rows, never
    flips loading. On page-1 only (pagedBeyond1Ref=false) it replaces rows;
    when paged beyond page 1 it updates counts only (so loadMore'd rows survive).
    On error it keeps existing rows silently. Guards on fetchingRef to avoid overlap.
</constraints>
*/
import { useState, useEffect, useCallback, useRef } from 'react';
import { TicketListItem, type PillFilterParams, type ActiveCounts } from '../serverTypes';
import { smarttix } from '../services/api';
import type { ActiveBucket } from '../services/lifecycleBuckets';

export type ActiveSort = 'uploaded' | 'received' | 'updated' | 'rejections' | 'zone';
export type ActiveDir = 'desc' | 'asc';

// part 7, 2026-10-11: the pill filters (flag / served search ids; part 7 fix loop, 2026-10-12: hasForm) ride beside the rest —
// declared once in api.ts (PillFilterParams), so this hook forwards them without a second spelling.
export interface ActiveFilters extends PillFilterParams {
  search?: string;
  primaryAxis?: string | null;
  secondaryAxis?: string[];
  from?: string | null;
  to?: string | null;
  uploadedBy?: string;
  // Classified-alongside ATTRIBUTE filters (wim vocabulary v7, 2026-09-13). Already translated to
  // the wire field spelling by services/ticketAttributes' attributeFilterParams — the page holds the
  // served-name-keyed record, this layer only forwards params.
  sparte?: string[];
  senderKind?: string[];
  // Sender-identity registry (P2, 2026-09-13), Task R2 — the organisation filter, comma-joined
  // like sparte/senderKind by the api layer. Held separately from AttributeFilter (the page's own
  // state, distinct from attributeFilterParams): the matched organisation is a registry row, not a
  // classified-alongside attribute, so it never rides ticketAttributes' translation table.
  senderOrganisationId?: string[];
}

// The counts shape is declared once, in api.ts, beside the list read that serves it.
export type { ActiveCounts };

// No `formBearing` here (part 7 fix loop, 2026-10-12): before the first answer nothing has counted
// form-bearing rows, and a fabricated 0 would be read as a measured one by the „Formular" filter
// bubble. Every reader treats the absent member as "not known" (the Excel batch: `?? 0` under its
// own countsLoading branch).
const EMPTY_COUNTS: ActiveCounts = { uploads: 0, offen: 0, entschieden: 0 };

export interface UseActivePagination {
  rows: TicketListItem[];
  counts: ActiveCounts;
  /** When the read that brought `counts` was ASKED (ms epoch; 0 = never). Both buckets' reads carry the
   *  whole counts object, so a page showing both tabs takes the NEWER of the two — see AntwortenSeite. */
  countsAt: number;
  loading: boolean;       // first-page fetch (bucket/sort/filter change)
  loadingMore: boolean;   // append fetch (sentinel)
  error: string | null;
  loadMore: () => void;
  // Callback ref: attach to the sentinel <div> at the bottom of the list.
  sentinelRef: (node: HTMLElement | null) => void;
  reset: () => void;
  /** Silent background page-1 refetch: never clears rows, never flips loading.
   *  Updates counts always; replaces rows only when on page 1 (not paged beyond).
   *  On error keeps existing rows silently. No-op if a fetch is already in flight. */
  refresh: () => void;
  removeRow: (uuid: string) => void;
  patchRow: (uuid: string, patch: Partial<TicketListItem>) => void;
}

export function useActivePagination(
  bucket: ActiveBucket,
  sort: ActiveSort,
  filters: ActiveFilters,
  dir: ActiveDir = 'desc',
): UseActivePagination {
  const [rows, setRows] = useState<TicketListItem[]>([]);
  const [counts, setCounts] = useState<ActiveCounts>(EMPTY_COUNTS);
  const [countsAt, setCountsAt] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Why: in-flight guard read inside loadMore without re-subscribing the
  // IntersectionObserver effect each render (which would thrash the observer).
  const fetchingRef = useRef(false);
  const cursorRef = useRef<string | null>(null);
  cursorRef.current = nextCursor;
  // Tracks whether the user has scrolled beyond page 1 via loadMore.
  // refresh() uses this to decide whether to replace rows (safe on page 1) or
  // update counts only (rows-replace would discard loadMore'd pages).
  const pagedBeyond1Ref = useRef(false);

  const { search, primaryAxis, secondaryAxis, from, to, uploadedBy, sparte, senderKind, senderOrganisationId, flag, searchPrimary, searchSecondary, hasForm } = filters;
  // secondaryAxis is a fresh array each render; key on its content so the first-page
  // effect doesn't re-fire on every parent render.
  const secondaryAxisKey = (secondaryAxis ?? []).join(',');
  // Same content-keying reason as secondaryAxisKey above — these arrive as fresh arrays each render.
  const sparteKey = (sparte ?? []).join(',');
  const senderKindKey = (senderKind ?? []).join(',');
  const senderOrganisationIdKey = (senderOrganisationId ?? []).join(',');
  const flagKey = (flag ?? []).join(',');
  const searchPrimaryKey = (searchPrimary ?? []).join(',');
  const searchSecondaryKey = (searchSecondary ?? []).join(',');
  // The pill filters arrive ALREADY normalised (searchFaceIds.pillFilterParams: empty lists are
  // undefined, hasForm is true or undefined) and appendPillFilterParams re-checks on the wire, so
  // both fetches forward this one object as-is (final review, part 7, 2026-10-11).
  const pillParams: PillFilterParams = { flag, searchPrimary, searchSecondary, hasForm };

  const fetchPage = useCallback(async (cursor: string | null) => {
    fetchingRef.current = true;
    if (cursor) setLoadingMore(true); else setLoading(true);
    setError(null);
    // A read is stamped with the moment it was ASKED, not answered: the list page compares it with
    // the moment a press was confirmed (the press, 2026-10-28) — a read asked before the server took
    // the press cannot know of it, however late it answers.
    const issued = Date.now();
    try {
      const res = await smarttix.getActivePage({
        bucket,
        sort,
        dir,
        search: search || undefined,
        primaryAxis: primaryAxis || undefined,
        secondaryAxis: secondaryAxis && secondaryAxis.length > 0 ? secondaryAxis : undefined,
        sparte: sparte && sparte.length > 0 ? sparte : undefined,
        senderKind: senderKind && senderKind.length > 0 ? senderKind : undefined,
        senderOrganisationId: senderOrganisationId && senderOrganisationId.length > 0 ? senderOrganisationId : undefined,
        ...pillParams,
        from: from || undefined,
        to: to || undefined,
        uploadedBy: uploadedBy || undefined,
        cursor: cursor || undefined,
      });
      setNextCursor(res.nextCursor);
      if (res.counts) { setCounts(res.counts); setCountsAt(issued); }
      if (cursor) {
        // Append, de-duping by uuid (sentinel can fire twice before state settles).
        pagedBeyond1Ref.current = true;
        setRows(prev => {
          const seen = new Set(prev.map(r => r.uuid));
          return [...prev, ...res.rows.filter(r => !seen.has(r.uuid))];
        });
      } else {
        pagedBeyond1Ref.current = false;
        setRows(res.rows);
      }
    } catch {
      setError('Fehler beim Laden der aktiven Tickets');
      if (!cursor) setRows([]);
    } finally {
      fetchingRef.current = false;
      if (cursor) setLoadingMore(false); else setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, sort, dir, search, primaryAxis, secondaryAxisKey, from, to, uploadedBy, sparteKey, senderKindKey, senderOrganisationIdKey, flagKey, searchPrimaryKey, searchSecondaryKey, hasForm]);

  // First page on mount + on every bucket/sort/dir/filter change (cursor reset to null).
  useEffect(() => {
    fetchPage(null);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (fetchingRef.current) return;
    if (!cursorRef.current) return;
    fetchPage(cursorRef.current);
  }, [fetchPage]);

  const reset = useCallback(() => {
    fetchPage(null);
  }, [fetchPage]);

  // Silent background page-1 refetch — reads filter state from the closure captured
  // in the useCallback dep array (same as fetchPage, so always fresh).
  const refresh = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const issued = Date.now();
    try {
      const res = await smarttix.getActivePage({
        bucket,
        sort,
        dir,
        search: search || undefined,
        primaryAxis: primaryAxis || undefined,
        secondaryAxis: secondaryAxis && secondaryAxis.length > 0 ? secondaryAxis : undefined,
        sparte: sparte && sparte.length > 0 ? sparte : undefined,
        senderKind: senderKind && senderKind.length > 0 ? senderKind : undefined,
        senderOrganisationId: senderOrganisationId && senderOrganisationId.length > 0 ? senderOrganisationId : undefined,
        ...pillParams,
        from: from || undefined,
        to: to || undefined,
        uploadedBy: uploadedBy || undefined,
        cursor: undefined,
      });
      setCounts(res.counts ?? EMPTY_COUNTS);
      setCountsAt(issued);
      setNextCursor(res.nextCursor);
      // Only replace rows when not paged beyond page 1 — replacing would drop
      // load-more'd rows that the user has scrolled to.
      if (!pagedBeyond1Ref.current) {
        pagedBeyond1Ref.current = false;
        setRows(res.rows);
      }
    } catch {
      // Keep existing rows + counts; do NOT set blocking error or toggle loading.
    } finally {
      fetchingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, sort, dir, search, primaryAxis, secondaryAxisKey, from, to, uploadedBy, sparteKey, senderKindKey, senderOrganisationIdKey, flagKey, searchPrimaryKey, searchSecondaryKey, hasForm]);

  const removeRow = useCallback((uuid: string) => {
    setRows(prev => prev.filter(r => r.uuid !== uuid));
  }, []);

  // A uuid this list does not hold leaves the array IDENTICAL — not merely unmodified (round-6
  // review, 2026-09-09). Callers now legitimately patch a row without knowing which of the two
  // buckets holds it (see AntwortenSeite's patchRowInBothBuckets), so the miss is the common case
  // rather than the odd one; `map` alone would hand React a fresh array every time and re-render
  // the other bucket's whole list for nothing.
  const patchRow = useCallback((uuid: string, patch: Partial<TicketListItem>) => {
    setRows(prev => prev.some(r => r.uuid === uuid)
      ? prev.map(r => r.uuid === uuid ? { ...r, ...patch } : r)
      : prev);
  }, []);

  // ─── IntersectionObserver sentinel ─────────────────────────────────────────
  // Callback ref so the observer (re)attaches the moment the sentinel node mounts
  // or unmounts — a static useRef + mount-only effect would miss it (the sentinel
  // only renders once the first page has rows). loadMore self-guards on cursor.
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) loadMoreRef.current();
    });
    obs.observe(node);
    observerRef.current = obs;
  }, []);

  return { rows, counts, countsAt, loading, loadingMore, error, loadMore, sentinelRef, reset, refresh, removeRow, patchRow };
}
